/* ============================================================
   PHONE AUTOMATION — SETUP & TEST (page pelanggan, universal)
   ============================================================
   PERANAN BARU (keputusan user 27 Okt): page ini BUKAN tempat posting
   sebenar. Ia tempat **setup + test** — macam "My Agent step 4":
     1. Pilih app (Shopee dulu; FB/TikTok menyusul).
     2. Connect telefon (grant sekali — browser ingat lepas ni).
     3. Pilih SATU card yang sudah ada video → "Run test" → hantar video
        card itu ke telefon + buka app → user lihat ia jadi ke tak.

   Posting SEBENAR = bulk dari workspace: tab Schedule → tick card → RUN
   (enjin bulk, bukan di sini). Page ini cuma mengesahkan telefon + app
   sedia untuk pelanggan itu (setiap telefon/versi app beza UI).

   ⛔ Muat card = SEMUA produk (sama macam workspace), BUKAN `orderBy
   lastUpdated` + limit — itu singkir produk tanpa cap lastUpdated / had 60
   (bug disahkan 27 Okt). Tapis client ke card yang ada ≥1 video.

   ⚠️ JUJUR: connect + hantar video + buka app = jalan. Auto-post hands-off
   (isi caption/produk + terbit tanpa pegang) = enjin belum siap.

   UI = English (i18n + pil EN/BM sebelum release). Rupa v3.9.
   ============================================================ */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, RefreshCw, Loader2, Film, PlayCircle, CircleCheck, CircleSlash, ChevronDown, ChevronUp, Circle, Sparkles, Camera, Home, ArrowLeft } from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { PageHeader, Btn } from '../design/ui';
import './PhoneAutomationView.css';
import { PHONE_APPS, phoneApp as findApp, bacaPhoneAiLabel, bacaPhoneFbPage, bacaPhoneFbLink, bacaPhoneTtBag } from '../utils/phoneApps';
import { AndroidController, isWebUsbSupported, AdbError, phoneVideoDest, lepaskanTelefon } from '../utils/androidAdb';
import { runRecipe } from '../utils/androidRunner';
import { recipeFor } from '../utils/androidRecipe';
import { captionUntukPhone, videoCardPalingBaru } from '../utils/androidPostCheck';
// ★ [2026-11-04] Ajar robot (Step 3 · Configure) — ajaran disimpan dalam akaun pelanggan.
import { muatAjaran, buangAjaran } from '../utils/androidAjar';

// Cap masa Firestore / nombor / tiada → millis (tiada = 0 → bawah senarai).
function msOf(t) {
  if (t && typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t === 'number') return t;
  return 0;
}

export default function PhoneAutomationView() {
  const navigate = useNavigate();
  const supported = isWebUsbSupported();
  // Pilihan app dari Control Panel (disimpan per-browser). Fallback app READY
  // kalau tetapan lama tersimpan app belum-sedia (elak "Soon" nampak terpilih).
  const [appId, setAppId] = useState(() => {
    try {
      const v = localStorage.getItem('sesaat_phone_app_v1');
      const a = PHONE_APPS.find((x) => x.id === v);
      return a && a.ready ? a.id : findApp('').id;
    } catch { return findApp('').id; }
  });
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [pickCard, setPickCard] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | working | opened | failed
  const [note, setNote] = useState('');
  // Stepper kalibrasi: satu baris per langkah resipi, status hidup.
  const [testSteps, setTestSteps] = useState([]); // [{id,label,status,note}]
  const [screen, setScreen] = useState(''); // mirror: dataUrl screenshot telefon terkini
  const ctlRef = useRef(null);
  // Ajar robot: permintaan semasa (robot tersekat) + senarai butang yang pelanggan sudah ajar.
  const [ajarMinta, setAjarMinta] = useState(null); // { label, langkah, dataUrl, skrin, cubaan }
  const ajarResolve = useRef(null);
  const [ajaran, setAjaran] = useState({});
  const imgAjarRef = useRef(null);

  const app = useMemo(() => findApp(appId), [appId]);
  const say = useCallback((msg) => setNote(msg), []);
  const muatSemulaAjaran = useCallback(async () => { setAjaran(await muatAjaran(appId)); }, [appId]);
  useEffect(() => { muatSemulaAjaran(); }, [muatSemulaAjaran]);

  // Robot minta pelanggan tunjuk butang → janji diselesaikan bila pelanggan klik gambar (atau batal).
  const mintaAjar = useCallback((req) => new Promise((resolve) => {
    ajarResolve.current = resolve;
    setAjarMinta(req);
    if (req.dataUrl) setScreen(req.dataUrl);
  }), []);
  const jawabAjar = useCallback((titik) => {
    const f = ajarResolve.current; ajarResolve.current = null;
    setAjarMinta(null);
    if (f) f(titik);
  }, []);
  const klikAjar = (e) => {
    const img = imgAjarRef.current;
    if (!img || !ajarMinta) return;
    const r = img.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * img.naturalWidth);
    const y = Math.round(((e.clientY - r.top) / r.height) * img.naturalHeight);
    jawabAjar({ x, y });
  };
  const resetAjaran = async (kunci) => {
    try { await buangAjaran(appId, kunci); } catch { say('Could not reset that step. Try again.'); }
    muatSemulaAjaran();
  };

  const getCtl = useCallback(() => {
    if (!ctlRef.current) {
      ctlRef.current = new AndroidController(() => {}, () => { setConnected(false); setDeviceName(''); });
    }
    return ctlRef.current;
  }, []);

  useEffect(() => () => { if (ctlRef.current) ctlRef.current.disconnect().catch(() => {}); }, []);

  const handleConnect = useCallback(async (reconnect) => {
    if (busy) return;
    setBusy(true); setPhase('idle');
    try {
      const ctl = getCtl();
      // Phone pernah dibenarkan pada browser ini → sambung terus tanpa senarai peranti Chrome.
      if (reconnect) await ctl.reconnect();
      else {
        try { await ctl.reconnect(); } catch (e) { if (e?.code === 'NO_GRANTED') await ctl.connect(); else throw e; }
      }
      const info = await ctl.getDeviceInfo();
      setConnected(true);
      setDeviceName(`${info.manufacturer || ''} ${info.model || ''}`.trim());
      say('Phone connected. You can now run a test.');
    } catch (e) {
      setConnected(false);
      say(e instanceof AdbError ? e.message : 'Could not connect. Check the cable and USB debugging.');
      setHelp(true);
    } finally { setBusy(false); }
  }, [busy, getCtl, say]);

  const handleRelease = useCallback(async () => {
    if (busy) return;
    try { await ctlRef.current?.disconnect(); await lepaskanTelefon(); } catch { /* lepas sedapat mungkin */ }
    setConnected(false);
    say('Phone released. You can now connect it in another browser.');
  }, [busy, say]);

  // Muat card yang ada video — SEMUA produk (macam workspace), tapis client.
  const loadCards = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) { say('Please sign in to load your cards.'); return; }
    setLoadingCards(true);
    try {
      const snap = await getDocs(collection(db, 'users', uid, 'products'));
      const out = [];
      snap.forEach((d) => {
        const data = d.data() || {}; const pr = data.prompts || {};
        // card diambil sekali — set PERTAMA yang ada video (test perlu 1 video).
        const sk = ['set1', 'set2', 'set3'].find(
          (k) => typeof pr[k]?.uploadedVideo === 'string' && /^https?:\/\//.test(pr[k].uploadedVideo),
        );
        if (!sk) return;
        out.push({ id: d.id, name: data.name || d.id, setKey: sk, url: pr[sk].uploadedVideo, ms: msOf(data.lastUpdated), caption: captionUntukPhone(pr[sk]), link: (data.shopeeLink || data.link || '').trim(), shopeeLink: String(data.shopeeLink || data.link || '').trim(), ttLink: String(data.link || '').trim() });
      });
      out.sort((a, b) => b.ms - a.ms);
      setCards(out);
      setPickCard((p) => (out.some((c) => c.id === p) ? p : (out[0]?.id || '')));
      if (!out.length) say('No cards with a video yet. Generate a video first, then come back.');
    } catch { say('Could not load your cards. Try Reload.'); }
    finally { setLoadingCards(false); }
  }, [say]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const selected = useMemo(() => cards.find((c) => c.id === pickCard) || null, [cards, pickCard]);

  // Test = larian KALIBRASI penuh. Hantar video → jalankan resipi hujung-ke-
  // hujung (SELALU berakhir Draft, tak pernah terbit) → Stepper tunjuk tiap
  // langkah hidup; error yang AI betulkan = hijau (self-healed). Lulus =
  // telefon "ready" + peta tersimpan. Posting sebenar = bulk dari workspace.
  const runTest = useCallback(async () => {
    if (!app.ready) { say(`${app.label} is coming soon.`); return; }
    if (!connected) { say('Connect your phone first.'); return; }
    if (!selected) { say('Pick a card with a video first.'); return; }
    if (busy) return;
    const recipe = recipeFor(appId);
    if (!recipe) { say(`No recipe for ${app.label} yet.`); return; }
    // TikTok / Facebook (enjin 'dump'): ikut tetapan Control Panel (AI label, page, link Shopee,
    // beg kuning) + Ajar robot. Shopee: laluan kalibrasi lama, tidak berubah.
    const dump = recipe.engine === 'dump';
    const opt = dump ? {
      page: app.fbOptions ? bacaPhoneFbPage() : '',
      link: !!(app.fbOptions && bacaPhoneFbLink() && /shopee|shp\.ee/i.test(selected.shopeeLink || '')),
      bag: !!(app.bag && bacaPhoneTtBag() && selected.ttLink),
    } : {};
    const senaraiLangkah = dump && recipe.stepsFor ? recipe.stepsFor('video', opt) : recipe.steps;
    setBusy(true); setPhase('working');
    setTestSteps(senaraiLangkah.map((s) => ({ id: s.id, label: s.labelEn, status: 'pending', note: '' })));
    try {
      const ctl = getCtl();
      const serial = ctl.info?.serial || ctl.device?.serial || '';
      say('Sending the video to your phone…');
      const res = await fetch(selected.url);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const dest = phoneVideoDest(selected.name, selected.id, selected.setKey);
      await ctl.pushVideo(blob, dest, () => {});
      await ctl.mediaScan(dest);
      if (dump && !(await videoCardPalingBaru(ctl, dest))) throw new Error('The card video is not the newest video on the phone yet. Try again.');
      say('Running a full test post on your phone — it will be saved as a Draft (never published).');
      // Ujian admin: ?ajarUji=tt.drafts,fb.create → anggap default butang itu gagal (paparkan Ajar robot).
      let paksaGagal;
      let ajaranTakSimpan = false;
      try { const q = new URLSearchParams(window.location.search).get('ajarUji'); if (q) paksaGagal = new Set(q.split(',').map((s) => s.trim()).filter(Boolean)); } catch { /* biar */ }
      const result = await runRecipe(ctl, {
        appId, serial, calibrating: true,
        params: dump
          ? {
            content: 'video', caption: selected.caption || selected.name || 'SESAAT test',
            aiLabel: app.aiLabel ? bacaPhoneAiLabel() : undefined,
            fbPage: opt.page, productLink: opt.link ? selected.shopeeLink : '',
            begKuning: opt.bag, productName: selected.name, videoDest: dest,
          }
          // Shopee: caption ringkas dari nama card; produk dilangkau (ditala berasingan).
          : { caption: selected.caption || selected.name || 'SESAAT test', productLink: selected.link, videoDest: dest },
        ajar: dump ? { ajaran, mintaAjar, paksaGagal } : undefined,
      }, {
        onStep: (st) => setTestSteps((prev) => {
          const n = prev.slice();
          if (n[st.index]) n[st.index] = { ...n[st.index], status: st.status, note: st.note };
          return n;
        }),
        onScreen: (url) => setScreen(url), // mirror hidup
        onLog: (msg, level) => {
          if (level === 'err' && /simpan ajaran/i.test(msg)) ajaranTakSimpan = true;
          if (level === 'err' || level === 'ok') say(msg);
        },
      });
      // Kegagalan simpan ajaran mesti kekal kelihatan (bising) walaupun test lulus.
      const notaSimpan = ajaranTakSimpan ? ' ⚠️ But what you showed the robot could not be saved to your account — it will ask again next time.' : '';
      if (result.status === 'draft_saved' || result.status === 'done') {
        setPhase('opened');
        say(`Test passed — your phone is set up ✅. A draft was saved on ${app.label} (delete it later). Real posting runs from the workspace Schedule tab: tick your cards, then Run.${notaSimpan}`);
      } else if (result.draftSaved) {
        setPhase('failed');
        say(`A draft was saved on ${app.label}, but not everything was done (${(result.belumSiap || []).join(', ') || 'see the red step'}). Run the test again and show the robot the button it asks for.`);
      } else {
        setPhase('failed');
        say('Test could not finish — see the red step above. Nothing was published; your phone is safe. Run the test again and show the robot the button it asks for.');
      }
    } catch (e) {
      setPhase('failed');
      say(e instanceof AdbError ? e.message : (e?.message || 'Something went wrong. Try "Re-sync my phone".'));
    } finally {
      setBusy(false);
      if (ajarResolve.current) jawabAjar(null);
      muatSemulaAjaran();
    }
  }, [app, appId, connected, selected, busy, getCtl, say, ajaran, mintaAjar, muatSemulaAjaran, jawabAjar]);

  // Mirror: ambil satu screenshot telefon secara manual (butang Capture).
  const capture = useCallback(async () => {
    if (!connected) return;
    try { const s = await getCtl().screenshot(); if (s?.dataUrl) setScreen(s.dataUrl); } catch { /* biar */ }
  }, [connected, getCtl]);
  const phoneKey = useCallback((fn) => async () => {
    if (!connected) return;
    try { await getCtl()[fn](); setTimeout(() => { capture(); }, 500); } catch { /* biar */ }
  }, [connected, getCtl, capture]);

  return (
    <main className="sesaat-m-tight pa-page" style={{ padding: '26px 28px 48px' }}>
      <div className="pa-back">
        <Btn variant="ghost" size="sm" onClick={() => navigate('/workspace')}>← Back to workspace</Btn>
      </div>

      <PageHeader
        title="Phone automation — set up & test"
        subtitle="Connect your phone and run one test before posting. Real posting runs from the workspace."
        actions={(
          <span className={`s-badge ${connected ? 's-badge--ok' : 's-badge--neutral'}`}>
            {connected ? `Connected${deviceName ? ' · ' + deviceName : ''}` : 'Not connected'}
          </span>
        )}
      />

      {!supported && (
        <div className="pa-alert">Open SESAAT in Google Chrome on a desktop to use phone posting.</div>
      )}

      {/* Step 1 — Post to (app). ⛔ Nama "Step 1 · Post to / Step 2 · Connect phone / Step 3 · Configure"
          dipersetujui user 4 Nov untuk ayat sokongan — JANGAN tukar tanpa tanya. */}
      <section className="s-card pa-card">
        <p className="pa-sect">Step 1 · Post to</p>
        <div className="pa-apps">
          {PHONE_APPS.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`pa-app ${appId === a.id ? 'on' : ''} ${a.ready ? '' : 'soon'}`}
              onClick={() => a.ready && setAppId(a.id)}
              disabled={!a.ready || busy}
              title={a.ready ? a.label : `${a.label} — coming soon`}
            >
              <Smartphone size={18} aria-hidden="true" />
              <span>{a.label}</span>
              {!a.ready && <span className="pa-soon">Soon</span>}
            </button>
          ))}
        </div>
      </section>

      {/* 2 — Connect phone */}
      <section className="s-card pa-card">
        <div className="pa-rowhead">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Smartphone size={18} style={{ color: connected ? 'var(--s-ok)' : 'var(--s-ink-2)' }} />
            <b style={{ fontSize: 14 }}>Step 2 · Connect phone</b>
          </span>
          <span className="pa-btns">
            {connected && <button className="s-btn s-btn--sm" onClick={handleRelease} disabled={busy}>Release phone</button>}
            {!connected && <button className="s-btn s-btn--primary s-btn--sm" onClick={() => handleConnect(false)} disabled={!supported || busy}>{busy ? <Loader2 size={14} className="pa-spin" /> : <Smartphone size={14} />} Connect phone</button>}
            {connected && <button className="s-btn s-btn--sm" onClick={() => handleConnect(true)} disabled={busy}><RefreshCw size={14} /> Re-sync my phone</button>}
          </span>
        </div>
        <button className="pa-help" onClick={() => setHelp((v) => !v)} type="button">
          How to set up my phone {help ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {help && (
          <ol className="pa-steps">
            <li>Plug your phone into this computer with a USB cable.</li>
            <li>On your phone, turn on <b>USB debugging</b> (Settings → Developer options).</li>
            <li>Press <b>Connect phone</b>, pick your phone, then tap <b>Allow</b> on the phone.</li>
          </ol>
        )}
      </section>

      {/* 3 — Pick a card + run test */}
      <section className="s-card pa-card">
        <div className="pa-rowhead">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Film size={18} /><b style={{ fontSize: 14 }}>Step 3 · Configure</b></span>
          <button className="s-btn s-btn--sm" onClick={loadCards} disabled={loadingCards}>{loadingCards ? <Loader2 size={14} className="pa-spin" /> : <RefreshCw size={14} />} Reload</button>
        </div>
        {cards.length > 0 ? (
          <select className="s-select pa-select" value={pickCard} onChange={(e) => setPickCard(e.target.value)} disabled={busy}>
            {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <p className="pa-hint">{loadingCards ? 'Loading your cards…' : 'No cards with a video yet.'}</p>
        )}

        <div className="pa-actions">
          <button className="s-btn s-btn--primary" onClick={runTest} disabled={!app.ready || !connected || !selected || busy}>
            {busy && phase === 'working' ? <Loader2 size={16} className="pa-spin" /> : <PlayCircle size={16} />} Run test
          </button>
        </div>
        <p className="pa-hint" style={{ marginTop: 10 }}>
          Test runs one full post on your phone and <b>saves it as a Draft</b> (never published) to set your
          phone up. If the robot can&apos;t find a button on your phone, it stops and asks you to <b>show it</b> —
          it remembers your answer for next time. Real posting is done in the workspace <b>Schedule</b> tab — tick your cards, then press <b>Run</b>.
        </p>

        {/* Ajar robot — robot tersekat, minta pelanggan tunjuk butang pada gambar skrin. */}
        {ajarMinta && (
          <div className="pa-ajar" role="dialog" aria-label="Show the robot">
            <div className="pa-ajar__head">
              <b>The robot needs your help</b>
              <span className="pa-ajar__step">Step: {ajarMinta.langkah || '—'}</span>
            </div>
            <p className="pa-ajar__msg">
              Tap the <b>{ajarMinta.label}</b> on the picture of your phone below.
              {ajarMinta.cubaan > 1 && ' That did not work — please show it again.'}
            </p>
            {ajarMinta.dataUrl
              ? <img ref={imgAjarRef} className="pa-ajar__img" src={ajarMinta.dataUrl} alt="Your phone screen — tap the button" onClick={klikAjar} />
              : <p className="pa-hint">No picture of the screen. Cancel and try again.</p>}
            <div className="pa-ajar__btns">
              <button type="button" className="s-btn s-btn--sm" onClick={() => jawabAjar(null)}>Cancel test</button>
            </div>
          </div>
        )}

        {/* Butang yang pelanggan sudah ajar (disimpan dalam akaun). */}
        {Object.keys(ajaran).length > 0 && (
          <div className="pa-taught">
            <p className="pa-sect" style={{ marginBottom: 6 }}>Taught by you</p>
            <ul>
              {Object.entries(ajaran).map(([kunci, a]) => (
                <li key={kunci}>
                  <span className="pa-taught__dot" aria-hidden="true" />
                  <span className="pa-taught__lbl">{a.label || kunci}</span>
                  <span className="pa-taught__step">{a.langkah}</span>
                  <button type="button" className="s-btn s-btn--sm" disabled={busy} onClick={() => resetAjaran(kunci)}>Reset</button>
                </li>
              ))}
            </ul>
            <p className="pa-hint">Other buttons use the SESAAT default. Reset a step if the robot starts tapping the wrong place.</p>
          </div>
        )}

        {/* Dua lajur: kiri = Stepper + status · kanan = mirror skrin telefon
            (hidup masa Test; manual Capture / Home / Back). Tindan pada skrin kecil. */}
        <div className="pa-test-grid">
          <div className="pa-test-left">
            {testSteps.length > 0 && (
              <ol className="pa-stepper">
                {testSteps.map((st) => (
                  <li key={st.id} className={`pa-step pa-step--${st.status}`}>
                    <span className="pa-step__ic" aria-hidden="true">
                      {st.status === 'running' ? <Loader2 size={15} className="pa-spin" />
                        : st.status === 'pass' || st.status === 'healed' ? <CircleCheck size={15} />
                          : st.status === 'error' ? <CircleSlash size={15} />
                            : <Circle size={15} />}
                    </span>
                    <span className="pa-step__lbl">{st.label}</span>
                    {st.status === 'healed' && <span className="pa-step__tag"><Sparkles size={11} /> AI adjusted</span>}
                    {st.status === 'error' && st.note && <span className="pa-step__err" title={st.note}>{st.note}</span>}
                  </li>
                ))}
              </ol>
            )}

            {note && (
              <div className={`pa-note ${phase === 'failed' ? 'bad' : (phase === 'opened' || (connected && !busy)) ? 'good' : ''}`}>
                {/* Berpusing HANYA masa betul-betul sibuk. Dah connect / siap → tanda ✔,
                   bukan loader (elak nampak "tersangkut loading" padahal dah siap). */}
                {busy ? <Loader2 size={15} className="pa-spin" />
                  : phase === 'failed' ? <CircleSlash size={15} />
                    : (phase === 'opened' || connected) ? <CircleCheck size={15} />
                      : <Circle size={15} />}
                <span>{note}</span>
              </div>
            )}
          </div>

          <div className="pa-mirror">
            <div className="pa-mirror__bar">
              <span className="pa-mirror__title">Phone screen</span>
              <span className="pa-mirror__ctl">
                <button className="s-btn s-btn--sm" onClick={capture} disabled={!connected || busy} title="Capture screenshot" type="button"><Camera size={14} /></button>
                <button className="s-btn s-btn--sm" onClick={phoneKey('home')} disabled={!connected || busy} title="Home" type="button"><Home size={14} /></button>
                <button className="s-btn s-btn--sm" onClick={phoneKey('back')} disabled={!connected || busy} title="Back" type="button"><ArrowLeft size={14} /></button>
              </span>
            </div>
            <div className="pa-mirror__screen">
              {screen
                ? <img src={screen} alt="Phone screen" />
                : <span className="pa-mirror__empty"><Smartphone size={24} />No screen yet. Run a test, or press Capture.</span>}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
